/**
 * 后台 - 视频模型表单（创建/编辑）
 *
 * 左右两栏布局：
 * - 左栏：渠道配置 + 生成模式与能力
 * - 右栏：基础信息 + 默认值 + 计费
 */

import { useEffect, useMemo, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import {
  videoModelAdminApi,
  videoChannelAdminApi,
  type VideoModelAdminView,
  type VideoChannelAdminView,
  type ModeCapabilitiesMap,
  type ModeCapabilityFlags,
} from '../services/videoAdminApi';
import { videoApi, type VideoProviderMetaInfo } from '../services/videoApi';
import { MultiplierEditor } from './MultiplierEditor';
import toast from '../../../components/Toast';

interface Props {
  model: VideoModelAdminView | null;
  onClose: (changed: boolean) => void;
}

type VideoMode = 'text2video' | 'img2video' | 'first_last_frame';
type VideoBillingMode = 'per_second' | 'per_call';
type ModeChannelInfo = { channel: VideoChannelAdminView | null; meta: VideoProviderMetaInfo | null };
type ModeChannelInfoMap = Record<VideoMode, ModeChannelInfo>;

const MODE_LABELS: Record<VideoMode, string> = {
  text2video: '文生视频',
  img2video: '图生视频',
  first_last_frame: '首尾帧',
};

const CAPABILITY_LABELS: Record<keyof ModeCapabilityFlags, string> = {
  audio: '音频生成',
  audioReference: '参考音频',
  videoReference: '参考视频',
  cameraMovement: '镜头运动',
  motionIntensity: '运动幅度',
};

const STANDARD_RESOLUTIONS = ['480p', '720p', '1080p', '4k'] as const;
const DEFAULT_RESOLUTION_MULTIPLIERS: Record<string, number> = {
  '480p': 1,
  '720p': 1.1,
  '1080p': 1.2,
  '4k': 1.8,
};

export default function VideoModelForm({ model, onClose }: Props) {
  const isEdit = !!model;

  const [channels, setChannels] = useState<VideoChannelAdminView[]>([]);
  const [providersMeta, setProvidersMeta] = useState<VideoProviderMetaInfo[]>([]);

  // 基础信息
  const [modelId, setModelId] = useState(model?.modelId || '');
  const [name, setName] = useState(model?.name || '');
  const [description, setDescription] = useState(model?.description || '');
  const [isActive, setIsActive] = useState(model?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState(model?.sortOrder ?? 0);
  const [appendPrompt, setAppendPrompt] = useState(model?.appendPrompt || '');

  // 计费
  const [billingMode, setBillingMode] = useState<VideoBillingMode>(model?.billingMode ?? 'per_second');
  const [creditsPerSecondInput, setCreditsPerSecondInput] = useState(String(model?.creditsPerSecond ?? 6));
  const [creditsPerCallInput, setCreditsPerCallInput] = useState(String(model?.creditsPerCall ?? model?.creditsPerSecond ?? 6));
  const [audioMultiplierInput, setAudioMultiplierInput] = useState(String(model?.audioMultiplier ?? 1));
  const [creditsPerSecondInputInput, setCreditsPerSecondInputInput] = useState(String(model?.creditsPerSecondInput ?? 0));
  const [creditsPerImageInputInput, setCreditsPerImageInputInput] = useState(String(model?.creditsPerImageInput ?? 0));
  const [resolutionMultipliers, setResolutionMultipliers] = useState<Record<string, number>>(
    mergeResolutionMultipliers(model?.resolutionMultipliers)
  );
  const [disabledResolutions, setDisabledResolutions] = useState<string[]>(
    model?.disabledResolutions || []
  );
  const [modeMultipliers, setModeMultipliers] = useState<Record<string, number>>(
    model?.modeMultipliers || { text2video: 1, img2video: 1, first_last_frame: 1 }
  );

  // modeCapabilities
  const [modeCapabilities, setModeCapabilities] = useState<ModeCapabilitiesMap>(
    model?.modeCapabilities || {}
  );

  // 默认值
  const [defaultDuration, setDefaultDuration] = useState(model?.defaultDuration ?? 5);
  const [defaultResolution, setDefaultResolution] = useState(model?.defaultResolution || '720p');
  const [defaultAspectRatio, setDefaultAspectRatio] = useState(model?.defaultAspectRatio || '16:9');

  // 渠道
  const [defaultChannelId, setDefaultChannelId] = useState(model?.defaultChannelId || '');
  const [text2videoChannelId, setText2videoChannelId] = useState(model?.text2videoChannelId || '');
  const [img2videoChannelId, setImg2videoChannelId] = useState(model?.img2videoChannelId || '');
  const [firstLastFrameChannelId, setFirstLastFrameChannelId] = useState(model?.firstLastFrameChannelId || '');

  const [submitting, setSubmitting] = useState(false);

  // 单价解析（允许小数：1 积分 = 0.05 元，成本定价下低价模型每秒单价不足 1 积分）
  const parsePositiveNumberInput = (raw: string): number | null => {
    const num = Number(raw);
    if (!raw.trim() || !Number.isFinite(num) || num <= 0) return null;
    return num;
  };
  const parseNonNegativeNumberInput = (raw: string): number | null => {
    const num = Number(raw);
    if (!raw.trim() || !Number.isFinite(num) || num < 0) return null;
    return num;
  };

  const creditsPerSecond = parsePositiveNumberInput(creditsPerSecondInput);
  const creditsPerCall = parsePositiveNumberInput(creditsPerCallInput);
  const activeCreditsInvalid = billingMode === 'per_call' ? creditsPerCall == null : creditsPerSecond == null;

  const audioMultiplier = parsePositiveNumberInput(audioMultiplierInput);
  const creditsPerSecondInputValue = parseNonNegativeNumberInput(creditsPerSecondInputInput);
  const creditsPerImageInputValue = parseNonNegativeNumberInput(creditsPerImageInputInput);

  useEffect(() => {
    videoChannelAdminApi.list().then(setChannels).catch(() => {});
    videoApi.getProviderMetadata().then(setProvidersMeta).catch(() => {});
  }, []);

  // 计算每个模式实际使用的渠道及其 provider metadata
  const modeChannelInfo = useMemo(() => {
    const result: ModeChannelInfoMap = {
      text2video: { channel: null, meta: null },
      img2video: { channel: null, meta: null },
      first_last_frame: { channel: null, meta: null },
    };

    const channelMap: Record<VideoMode, string> = {
      text2video: text2videoChannelId,
      img2video: img2videoChannelId,
      first_last_frame: firstLastFrameChannelId,
    };

    for (const mode of ['text2video', 'img2video', 'first_last_frame'] as VideoMode[]) {
      let ch = channels.find((c) => c.id === channelMap[mode]) || null;
      if (!ch && defaultChannelId) {
        ch = channels.find((c) => c.id === defaultChannelId) || null;
      }
      if (ch) {
        const meta = providersMeta.find((p) => p.id === ch!.provider) || null;
        result[mode] = { channel: ch, meta };
      }
    }

    return result;
  }, [channels, providersMeta, defaultChannelId, text2videoChannelId, img2videoChannelId, firstLastFrameChannelId]);

  const providerSupportedResolutions = useMemo(
    () => collectSupportedResolutions(modeChannelInfo),
    [modeChannelInfo]
  );

  const supportedResolutions = useMemo(
    () => providerSupportedResolutions.filter((item) => !isResolutionDisabled(item, disabledResolutions)),
    [providerSupportedResolutions, disabledResolutions]
  );

  const visibleResolutionMultipliers = useMemo(() => {
    if (supportedResolutions.length === 0) return resolutionMultipliers;
    return pickResolutionMultipliers(resolutionMultipliers, supportedResolutions);
  }, [resolutionMultipliers, supportedResolutions]);

  useEffect(() => {
    if (providerSupportedResolutions.length === 0) return;

    setResolutionMultipliers((prev) => {
      const next = pickResolutionMultipliers(prev, providerSupportedResolutions);
      return shallowEqualNumberMap(prev, next) ? prev : next;
    });

    setDefaultResolution((prev) => {
      const normalized = String(prev || '').trim().toLowerCase();
      const matched = supportedResolutions.find((item) => item.toLowerCase() === normalized);
      return matched || supportedResolutions[0] || prev;
    });
  }, [providerSupportedResolutions, supportedResolutions]);

  const isModeAvailable = (mode: VideoMode): boolean => {
    const info = modeChannelInfo[mode];
    if (!info.meta) return false;
    return info.meta.modeSupport[mode] === true;
  };

  const isCapabilityAvailable = (mode: VideoMode, cap: keyof ModeCapabilityFlags): boolean => {
    const info = modeChannelInfo[mode];
    if (!info.meta) return false;
    return info.meta.capabilitySupport[cap] === true;
  };

  const toggleMode = (mode: VideoMode, enabled: boolean) => {
    setModeCapabilities((prev) => {
      const next = { ...prev };
      if (enabled) {
        next[mode] = next[mode] || { audio: false, audioReference: false, videoReference: false, cameraMovement: false, motionIntensity: false };
      } else {
        delete next[mode];
      }
      return next;
    });
  };

  const toggleCapability = (mode: VideoMode, cap: keyof ModeCapabilityFlags, enabled: boolean) => {
    setModeCapabilities((prev) => {
      const next = { ...prev };
      if (next[mode]) {
        next[mode] = { ...next[mode], [cap]: enabled };
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!modelId.trim()) return toast.error('请填写模型 ID');
    if (!name.trim()) return toast.error('请填写模型名称');
    if (creditsPerSecond == null) return toast.error('每秒积分必须是大于 0 的数字');
    if (creditsPerCall == null) return toast.error('每次积分必须是大于 0 的数字');
    if (audioMultiplier == null) return toast.error('音频倍率必须是大于 0 的数字');
    if (creditsPerSecondInputValue == null) return toast.error('参考视频每秒积分必须是不小于 0 的数字');
    if (creditsPerImageInputValue == null) return toast.error('参考图每张积分必须是不小于 0 的数字');
    if (providerSupportedResolutions.length > 0 && supportedResolutions.length === 0) {
      return toast.error('请至少保留一个启用分辨率');
    }

    setSubmitting(true);
    try {
      const payload = {
        modelId: modelId.trim(),
        name: name.trim(),
        description: description.trim() || null,
        isActive,
        sortOrder: Number(sortOrder) || 0,
        appendPrompt: appendPrompt.trim() || null,
        billingMode,
        creditsPerSecond,
        creditsPerCall,
        audioMultiplier,
        creditsPerSecondInput: creditsPerSecondInputValue,
        creditsPerImageInput: creditsPerImageInputValue,
        resolutionMultipliers: providerSupportedResolutions.length > 0
          ? pickResolutionMultipliers(resolutionMultipliers, providerSupportedResolutions)
          : resolutionMultipliers,
        disabledResolutions: normalizeResolutionList(
          disabledResolutions.filter((item) => providerSupportedResolutions.includes(item))
        ),
        modeMultipliers,
        modeCapabilities,
        defaultDuration: Number(defaultDuration) || 5,
        defaultResolution: defaultResolution.trim() || '720p',
        defaultAspectRatio: defaultAspectRatio.trim() || '16:9',
        defaultChannelId: defaultChannelId || null,
        text2videoChannelId: text2videoChannelId || null,
        img2videoChannelId: img2videoChannelId || null,
        firstLastFrameChannelId: firstLastFrameChannelId || null,
      } as any;

      if (isEdit && model) {
        await videoModelAdminApi.update(model.id, payload);
        toast.success('已保存');
      } else {
        await videoModelAdminApi.create(payload);
        toast.success('已创建');
      }
      onClose(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-6 px-4"
      onClick={() => onClose(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-light-text">
            {isEdit ? '编辑视频模型' : '新增视频模型'}
          </h3>
          <button onClick={() => onClose(false)} className="p-1.5 rounded-lg hover:bg-light-hover">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 三栏内容 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:divide-x divide-light-border">
          {/* 左栏：基础信息 + 默认值 */}
          <div className="p-6 space-y-6">
            <Section title="基础信息">
              <Field label="模型 ID（唯一）">
                <input
                  type="text"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  className="input-field w-full font-mono text-sm"
                  placeholder="seedance-2-0-mini"
                  disabled={isEdit}
                />
              </Field>
              <Field label="显示名称">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-field w-full"
                />
              </Field>
              <Field label="描述">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="input-field w-full"
                />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="排序">
                  <input
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(Number(e.target.value))}
                    className="input-field w-full"
                  />
                </Field>
                <Field label="prompt 后缀">
                  <input
                    type="text"
                    value={appendPrompt}
                    onChange={(e) => setAppendPrompt(e.target.value)}
                    className="input-field w-full"
                    placeholder="可选"
                  />
                </Field>
              </div>
              <Toggle label="启用此模型" value={isActive} onChange={setIsActive} />
            </Section>

            <Section title="前端默认值">
              <div className="grid grid-cols-3 gap-3">
                <Field label="时长（秒）">
                  <input
                    type="number"
                    value={defaultDuration}
                    onChange={(e) => setDefaultDuration(Number(e.target.value))}
                    className="input-field w-full"
                  />
                </Field>
                <Field label="分辨率">
                  {supportedResolutions.length > 0 ? (
                    <>
                      <select
                        value={defaultResolution}
                        onChange={(e) => setDefaultResolution(e.target.value)}
                        className="input-field w-full"
                      >
                        {supportedResolutions.map((resolution) => (
                          <option key={resolution} value={resolution}>
                            {resolution}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-light-muted mt-1">
                        当前支持：{supportedResolutions.join(' / ')}
                      </p>
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={defaultResolution}
                        onChange={(e) => setDefaultResolution(e.target.value)}
                        className="input-field w-full"
                      />
                      <p className="text-xs text-amber-600 mt-1">
                        还未从当前渠道解析到支持分辨率，请先选择视频渠道。
                      </p>
                    </>
                  )}
                </Field>
                <Field label="比例">
                  <input
                    type="text"
                    value={defaultAspectRatio}
                    onChange={(e) => setDefaultAspectRatio(e.target.value)}
                    className="input-field w-full"
                  />
                </Field>
              </div>
              {providerSupportedResolutions.length > 0 && (
                <Field label="禁用分辨率">
                  <div className="flex flex-wrap gap-2">
                    {providerSupportedResolutions.map((resolution) => {
                      const disabled = isResolutionDisabled(resolution, disabledResolutions);
                      return (
                        <label
                          key={resolution}
                          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${
                            disabled
                              ? 'border-rose-200 bg-rose-50 text-rose-700'
                              : 'border-light-border bg-white text-light-text'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={disabled}
                            onChange={(e) => {
                              setDisabledResolutions((prev) =>
                                e.target.checked
                                  ? normalizeResolutionList([...prev, resolution])
                                  : prev.filter((item) => item.toLowerCase() !== resolution.toLowerCase())
                              );
                            }}
                          />
                          <span>{resolution}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-light-muted mt-2">
                    这里禁用后，前台用户、公开能力接口和后端校验都会一起收紧。
                  </p>
                </Field>
              )}
            </Section>
          </div>

          {/* 中栏：渠道配置 + 模式能力 */}
          <div className="p-6 space-y-6">
            <Section title="API 渠道配置">
              <Field label="默认渠道">
                <ChannelSelect channels={channels} value={defaultChannelId} onChange={setDefaultChannelId} />
              </Field>
              <Field label="文生视频专用渠道">
                <ChannelSelect channels={channels} value={text2videoChannelId} onChange={setText2videoChannelId} />
              </Field>
              <Field label="图生视频专用渠道">
                <ChannelSelect channels={channels} value={img2videoChannelId} onChange={setImg2videoChannelId} />
              </Field>
              <Field label="首尾帧专用渠道">
                <ChannelSelect channels={channels} value={firstLastFrameChannelId} onChange={setFirstLastFrameChannelId} />
              </Field>
            </Section>

            <Section title="生成模式与能力">
              {channels.length === 0 && (
                <p className="text-sm text-light-muted">请先配置渠道。</p>
              )}
              {(['text2video', 'img2video', 'first_last_frame'] as VideoMode[]).map((mode) => {
                const available = isModeAvailable(mode);
                const enabled = mode in modeCapabilities;
                const info = modeChannelInfo[mode];

                return (
                  <div key={mode} className="border border-light-border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-light-text">{MODE_LABELS[mode]}</span>
                        {info.channel && (
                          <span className="text-xs text-light-muted">
                            → {info.channel.name}
                          </span>
                        )}
                      </div>
                      {available ? (
                        <Toggle label="" value={enabled} onChange={(v) => toggleMode(mode, v)} />
                      ) : (
                        <span className="text-xs text-light-muted">无可用渠道</span>
                      )}
                    </div>

                    {enabled && available && (
                      <div className="pl-4 grid grid-cols-2 gap-1">
                        {(Object.keys(CAPABILITY_LABELS) as Array<keyof ModeCapabilityFlags>).map((cap) => {
                          const capAvailable = isCapabilityAvailable(mode, cap);
                          if (!capAvailable) return null;
                          const capEnabled = modeCapabilities[mode]?.[cap] ?? false;
                          return (
                            <Toggle
                              key={cap}
                              label={CAPABILITY_LABELS[cap]}
                              value={capEnabled}
                              onChange={(v) => toggleCapability(mode, cap, v)}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </Section>
          </div>

          {/* 右栏：计费 */}
          <div className="p-6 space-y-6">
            <Section title="计费">
              <Field label="计费方式">
                <select
                  value={billingMode}
                  onChange={(e) => setBillingMode(e.target.value as VideoBillingMode)}
                  className="input-field w-full"
                >
                  <option value="per_second">按秒计费</option>
                  <option value="per_call">按次计费</option>
                </select>
                <p className="text-xs text-light-muted mt-1">
                  每个视频模型固定选择一种计费方式，前台用户跟随所选模型自动扣费。
                </p>
              </Field>
              <Field label={billingMode === 'per_call' ? '每次积分' : '每秒积分'}>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={billingMode === 'per_call' ? creditsPerCallInput : creditsPerSecondInput}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    if (billingMode === 'per_call') {
                      setCreditsPerCallInput(nextValue);
                    } else {
                      setCreditsPerSecondInput(nextValue);
                    }
                  }}
                  className="input-field w-full"
                />
                <p className="text-xs text-light-muted mt-1">
                  {billingMode === 'per_call'
                    ? '扣费 = ceil(每次积分 × 分辨率倍率 × 音频倍率 × 模式倍率)'
                    : '扣费 = ceil(每秒积分 × 时长 × 分辨率倍率 × 音频倍率 × 模式倍率)'}
                </p>
                {activeCreditsInvalid && (
                  <p className="text-xs text-rose-600 mt-1">请输入大于 0 的整数积分。</p>
                )}
              </Field>
              <Field label="音频倍率">
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  inputMode="decimal"
                  value={audioMultiplierInput}
                  onChange={(e) => setAudioMultiplierInput(e.target.value)}
                  className="input-field w-full"
                />
                <p className="text-xs text-light-muted mt-1">
                  用户开启音频生成时乘入（1 = 不加价）。如上游音频档加价 25% 填 1.25、翻倍填 2。
                </p>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="参考视频每秒积分">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={creditsPerSecondInputInput}
                    onChange={(e) => setCreditsPerSecondInputInput(e.target.value)}
                    className="input-field w-full"
                  />
                  <p className="text-xs text-light-muted mt-1">
                    输入素材计费：ceil(单价 × 参考视频总时长 × 分辨率倍率)。0 = 不收。
                  </p>
                </Field>
                <Field label="参考图每张积分">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={creditsPerImageInputInput}
                    onChange={(e) => setCreditsPerImageInputInput(e.target.value)}
                    className="input-field w-full"
                  />
                  <p className="text-xs text-light-muted mt-1">
                    输入素材计费：单价 × 参考图张数（含首尾帧）。0 = 不收。
                  </p>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {supportedResolutions.length > 0 ? (
                  <MultiplierEditor
                    label="分辨率倍率"
                    keyPlaceholder="分辨率"
                    valuePlaceholder="倍率"
                    value={visibleResolutionMultipliers}
                    onChange={setResolutionMultipliers}
                    fixedKeys={supportedResolutions}
                    hint={`仅显示当前渠道支持的分辨率：${supportedResolutions.join(' / ')}`}
                  />
                ) : (
                  <div>
                    <label className="text-sm font-medium text-light-text">分辨率倍率</label>
                    <p className="text-xs text-amber-600 mt-1">
                      当前无法判断模型支持哪些分辨率。请先选择默认渠道或模式专用渠道后再设置价格。
                    </p>
                  </div>
                )}
                <MultiplierEditor
                  label="模式倍率"
                  keyPlaceholder="模式"
                  valuePlaceholder="倍率"
                  value={modeMultipliers}
                  onChange={setModeMultipliers}
                  fixedKeys={['text2video', 'img2video', 'first_last_frame']}
                  keyLabels={{ text2video: '文生视频', img2video: '图生视频', first_last_frame: '首尾帧' }}
                />
              </div>
              <CreditsPreview
                billingMode={billingMode}
                creditsPerSecond={creditsPerSecond}
                creditsPerCall={creditsPerCall}
                resolutionMultipliers={visibleResolutionMultipliers}
                modeMultipliers={modeMultipliers}
              />
            </Section>
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="px-6 py-4 border-t border-light-border flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={() => onClose(false)} className="btn-secondary">
            取消
          </button>
          <button onClick={handleSubmit} disabled={submitting} className="btn-primary">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3 pb-4 border-b border-light-border last:border-b-0 last:pb-0">
      <h4 className="font-semibold text-light-text">{title}</h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-sm font-medium text-light-text block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-light-text cursor-pointer">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
      {label}
    </label>
  );
}

function ChannelSelect({
  channels,
  value,
  onChange,
}: {
  channels: VideoChannelAdminView[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="input-field w-full">
      <option value="">— 不指定 —</option>
      {channels.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}（{c.providerLabel}）{c.apiModelId ? ` [${c.apiModelId}]` : ''}
        </option>
      ))}
    </select>
  );
}

const PREVIEW_DURATIONS = [4, 5, 6, 8, 10, 12, 15];
const DEFAULT_PREVIEW_DURATIONS = [5, 10];

function CreditsPreview({
  billingMode,
  creditsPerSecond,
  creditsPerCall,
  resolutionMultipliers,
  modeMultipliers,
}: {
  billingMode: VideoBillingMode;
  creditsPerSecond: number | null;
  creditsPerCall: number | null;
  resolutionMultipliers: Record<string, number>;
  modeMultipliers: Record<string, number>;
}) {
  const [rmbPerCreditStr, setRmbPerCreditStr] = useState(() => localStorage.getItem('video_rmb_per_credit') || '');
  const [visibleDurations, setVisibleDurations] = useState<number[]>(DEFAULT_PREVIEW_DURATIONS);
  const rmbPerCredit = parseFloat(rmbPerCreditStr) || 0;

  const handleRmbChange = (v: string) => {
    setRmbPerCreditStr(v);
    localStorage.setItem('video_rmb_per_credit', v);
  };
  const handleDurationToggle = (duration: number) => {
    setVisibleDurations((prev) => {
      if (prev.includes(duration)) {
        return prev.filter((item) => item !== duration);
      }
      return PREVIEW_DURATIONS.filter((item) => prev.includes(item) || item === duration);
    });
  };
  const resolutions = getOrderedResolutions(resolutionMultipliers);
  const modes = Object.keys(modeMultipliers);

  const baseCredits = billingMode === 'per_call' ? creditsPerCall : creditsPerSecond;

  if (resolutions.length === 0 || modes.length === 0 || baseCredits == null || baseCredits <= 0) {
    return null;
  }

  const modeLabels: Record<string, string> = {
    text2video: '文生',
    img2video: '图生',
    first_last_frame: '首尾帧',
  };

  const calc = (duration: number, res: string, mode: string) => {
    const resMul = resolutionMultipliers[res] ?? 1;
    const modeMul = modeMultipliers[mode] ?? 1;
    return Math.ceil(
      billingMode === 'per_call'
          ? baseCredits * resMul * modeMul
          : baseCredits * duration * resMul * modeMul
    );
  };

  const formatRmb = (credits: number) => {
    if (!rmbPerCredit) return '';
    return `¥${(credits * rmbPerCredit).toFixed(3)}`;
  };

  const calcUnitCredits = (res: string, mode: string) => {
    const resMul = resolutionMultipliers[res] ?? 1;
    const modeMul = modeMultipliers[mode] ?? 1;
    return baseCredits * resMul * modeMul;
  };

  const calcRoundedUnitCredits = (res: string, mode: string) => {
    return Math.ceil(calcUnitCredits(res, mode));
  };

  const RES_COLORS: Record<string, string> = {
    '480p': 'bg-rose-50',
    '720p': 'bg-emerald-50',
    '1080p': 'bg-amber-50',
    '4k': 'bg-violet-50',
  };
  const fallbackColors = ['bg-purple-50', 'bg-rose-50', 'bg-cyan-50'];

  const getResColor = (res: string, idx: number) => {
    return RES_COLORS[res] || fallbackColors[idx % fallbackColors.length] || '';
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-sm font-medium text-light-text">积分预览</span>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-md bg-light-hover px-2 py-0.5 text-light-secondary">
              {billingMode === 'per_call' ? '基础每次参考' : '基础每秒参考'}:
              <span className="font-mono text-light-text"> {Math.ceil(baseCredits)}</span>
            </span>
            {rmbPerCredit > 0 && (
              <span className="rounded-md bg-light-hover px-2 py-0.5 text-light-secondary">
                {billingMode === 'per_call' ? '基础每次参考价格' : '基础每秒参考价格'}:
                <span className="font-mono text-light-text">¥{(Math.ceil(baseCredits) * rmbPerCredit).toFixed(3)}</span>
              </span>
            )}
            <span className="rounded-md bg-amber-50 px-2 py-0.5 text-amber-700">
              预览按最终扣费向上取整
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-light-muted">显示时长:</span>
            {PREVIEW_DURATIONS.map((duration) => {
              const checked = visibleDurations.includes(duration);
              return (
                <label
                  key={duration}
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 transition-colors ${
                    checked
                      ? 'border-accent-blue bg-accent-blue/10 text-accent-blue'
                      : 'border-light-border bg-white text-light-secondary hover:border-accent-blue/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleDurationToggle(duration)}
                    className="h-3.5 w-3.5 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                  />
                  <span>{duration}s</span>
                </label>
              );
            })}
          </div>
          {billingMode === 'per_call' && (
            <div className="mt-2 text-xs text-light-muted">
              按次计费不受时长影响，下表各时长列会保持一致，主要用于和按秒模型对比。
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-light-muted">1积分 =</span>
          <input
            type="number"
            step="0.001"
            min="0"
            value={rmbPerCreditStr}
            onChange={(e) => handleRmbChange(e.target.value)}
            className="w-16 px-1.5 py-0.5 border border-light-border rounded text-xs text-center"
            placeholder="0"
          />
          <span className="text-light-muted">元</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-light-border rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-light-hover">
              <th className="px-2 py-1.5 text-left text-light-muted font-medium">模式 / 分辨率</th>
              {visibleDurations.map((d) => (
                <th key={d} className="px-2 py-1.5 text-center text-light-muted font-medium">{d}s</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modes.map((mode) =>
              resolutions.map((res, resIdx) => (
                <tr key={`${mode}-${res}`} className={`border-t border-light-border ${getResColor(res, resIdx)}`}>
                  <td className="px-2 py-1 text-light-secondary">
                    <div className="flex flex-col">
                      <span>{modeLabels[mode] || mode} · {res}</span>
                      <span className="text-[11px] text-light-muted">
                        {billingMode === 'per_call' ? '单次参考' : '单秒参考'} {calcRoundedUnitCredits(res, mode)} 积分
                        {rmbPerCredit > 0 && (
                          <span className="ml-1">
                            · ¥{(calcRoundedUnitCredits(res, mode) * rmbPerCredit).toFixed(3)}/
                            {billingMode === 'per_call' ? '次' : '秒'}
                          </span>
                        )}
                      </span>
                    </div>
                  </td>
                  {visibleDurations.map((d) => {
                    const credits = calc(d, res, mode);
                    return (
                      <td key={d} className="px-2 py-1 text-center">
                        <span className="text-light-text font-mono">{credits}</span>
                        {rmbPerCredit > 0 && (
                          <span className="text-light-muted ml-0.5">({formatRmb(credits)})</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getOrderedResolutions(resolutionMultipliers: Record<string, number>): string[] {
  const normalizedMap = new Map<string, string>();
  for (const key of Object.keys(resolutionMultipliers || {})) {
    const trimmed = String(key || '').trim();
    if (!trimmed) continue;
    normalizedMap.set(trimmed.toLowerCase(), trimmed);
  }

  const ordered = STANDARD_RESOLUTIONS
    .filter((value) => normalizedMap.has(value.toLowerCase()))
    .map((value) => normalizedMap.get(value.toLowerCase())!);

  const extras = Array.from(normalizedMap.entries())
    .filter(([key]) => !STANDARD_RESOLUTIONS.includes(key as typeof STANDARD_RESOLUTIONS[number]))
    .map(([, value]) => value)
    .sort((a, b) => a.localeCompare(b));

  return [...ordered, ...extras];
}


function normalizeResolutionList(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const trimmed = String(value || '').trim();
    if (!trimmed) continue;

    const normalized = trimmed.toLowerCase();
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(trimmed);
  }

  return result;
}

function isResolutionDisabled(resolution: string, disabledResolutions: string[]): boolean {
  const normalized = String(resolution || '').trim().toLowerCase();
  return disabledResolutions.some((item) => String(item || '').trim().toLowerCase() === normalized);
}

function collectSupportedResolutions(modeChannelInfo: ModeChannelInfoMap): string[] {
  const collected: Record<string, number> = {};

  for (const info of Object.values(modeChannelInfo)) {
    const resolutions =
      info.channel?.supportedResolutions && info.channel.supportedResolutions.length > 0
        ? info.channel.supportedResolutions
        : info.meta?.supportedResolutions || [];

    for (const resolution of resolutions) {
      const trimmed = String(resolution || '').trim();
      if (!trimmed) continue;
      collected[trimmed] = 1;
    }
  }

  return getOrderedResolutions(collected);
}

function pickResolutionMultipliers(
  resolutionMultipliers: Record<string, number>,
  supportedResolutions: string[]
): Record<string, number> {
  const normalizedValueMap = new Map<string, number>();

  for (const [key, value] of Object.entries(resolutionMultipliers || {})) {
    const trimmed = String(key || '').trim();
    if (!trimmed) continue;
    normalizedValueMap.set(trimmed.toLowerCase(), value);
  }

  const next: Record<string, number> = {};
  for (const resolution of supportedResolutions) {
    const existing = normalizedValueMap.get(resolution.toLowerCase());
    next[resolution] = Number.isFinite(existing) ? existing! : DEFAULT_RESOLUTION_MULTIPLIERS[resolution] ?? 1;
  }

  return next;
}

function shallowEqualNumberMap(a: Record<string, number>, b: Record<string, number>): boolean {
  const aKeys = Object.keys(a || {});
  const bKeys = Object.keys(b || {});
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }

  return true;
}

function mergeResolutionMultipliers(
  resolutionMultipliers?: Record<string, number> | null
): Record<string, number> {
  return {
    ...DEFAULT_RESOLUTION_MULTIPLIERS,
    ...(resolutionMultipliers || {}),
  };
}
